import { Box, Button, Typography } from "@mui/material";
import React from "react";

import { boostReplay, captureException } from "../utils/posthog";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Class component required: React has no hook equivalent for error boundaries.
export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // biome-ignore lint/suspicious/noConsole: intentional browser console output
    console.error("Uncaught render error", error, info.componentStack);
    captureException(error, { component_stack: info.componentStack });
    boostReplay("app_error");
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (!error) {
      return children;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          px: 3,
          textAlign: "center",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <Typography variant="h5" sx={{ color: "#25233F" }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" sx={{ color: "#5A5870", maxWidth: 480 }}>
          Please reload the page. We have been notified — if this keeps
          happening, contact Wieldy support.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    );
  }
}
